import { z } from "zod";
import { recipientsSchema } from "./recipients.js";
/** The CONFIG / CURRENT item (SPEC §5), as seen by the API and the SPA. */
export const configSchema = z.object({
    enabled: z.boolean(),
    pausedUntil: z.string().datetime().nullable(),
    recipients: z.array(z.string()),
    updatedBy: z.string(),
    updatedAt: z.string(),
    version: z.number().int().nonnegative(),
    /*
     * Set by the worker when its own failsafe trips: two consecutive runs that
     * each filled an entire alert. Both default, because the CONFIG item was
     * seeded before they existed.
     */
    haltedAt: z.string().nullable().default(null),
    haltReason: z.string().nullable().default(null),
});
/**
 * Computed server-side, never by the client (SPEC §7).
 *
 * NOTE — deviation from the literal formula in SPEC §6.2
 * (`enabled && (pausedUntil == null || now > pausedUntil)`). That expression
 * can never be true when `enabled` is false, which would make an expired
 * `pausedUntil` do nothing at all. It contradicts §5 ("when past, treat as
 * enabled again"), the §11 criterion "with pausedUntil in the past, sending
 * resumes with no manual toggle", and the whole point of the field per §2
 * ("a kill switch flipped on Friday and forgotten is the expected failure
 * mode"). The stated intent wins: an elapsed pause auto-resumes.
 */
export function isEffectivelyEnabled(config, now = new Date()) {
    if (config.pausedUntil != null) {
        const resumeAt = Date.parse(config.pausedUntil);
        // An unparseable timestamp must not silently re-enable sending.
        if (Number.isFinite(resumeAt) && now.getTime() > resumeAt)
            return true;
    }
    return config.enabled;
}
export function toConfigResponse(config, now = new Date()) {
    return { ...config, effectivelyEnabled: isEffectivelyEnabled(config, now) };
}
/** `PUT /config` request body. */
export const putConfigRequestSchema = z.object({
    recipients: recipientsSchema,
    version: z.number().int().nonnegative(),
});
export const MAX_PAUSE_DAYS = 30;
/** `POST /pause` request body. */
export const pauseRequestSchema = z
    .object({
    enabled: z.boolean(),
    pausedUntil: z.string().datetime().nullable().optional().default(null),
})
    .superRefine((value, ctx) => {
    if (value.pausedUntil == null)
        return;
    if (value.enabled) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["pausedUntil"],
            message: "'pausedUntil' is only meaningful when 'enabled' is false",
        });
        return;
    }
    const at = Date.parse(value.pausedUntil);
    if (!Number.isFinite(at)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["pausedUntil"],
            message: "'pausedUntil' is not a valid ISO-8601 timestamp",
        });
        return;
    }
    const now = Date.now();
    if (at <= now) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["pausedUntil"],
            message: "'pausedUntil' must be in the future",
        });
    }
    if (at > now + MAX_PAUSE_DAYS * 86_400_000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["pausedUntil"],
            message: `'pausedUntil' must be at most ${MAX_PAUSE_DAYS} days out`,
        });
    }
});
//# sourceMappingURL=config.js.map